// EditorHost — the material-content editing surface's impure shell
// (strangler-fig P3).
//
// Moved out of AppShellHost verbatim (.claude/analysis/ui-ddd-decomposition.md
// §6 Phase 3): owns the loaded material's edit state (elements/contentHash/
// pathById/materialProvenance/materialError), the compose wear (`loop`),
// undo/redo history, the review queue (proposals/reviewPendingId), the
// slash-menu's live state, and every structural-edit handler.
//
// SHAPE NOTE (same reasoning as OmnibarHost, Phase 2): this is a HOOK
// (`useEditorHost`), not a mounted `<Component/>`. `<MaterialDocument>` /
// `<MaterialToolbar>` / `<ProposalReview>` / `<Palette>` render deeply
// interleaved with shell-owned chrome (the dock, the tab strip, the rail
// overflow menu) inside AppShellHost's own render functions — there is no
// self-contained subtree to mount. AppShellHost keeps rendering that JSX,
// reading every editor-owned value/handler off this hook's returned API
// object instead of a bare local variable.
//
// CROSS-CLUSTER SEAMS (both deliberate, both documented where they're used):
//   - Quality/policy stay OUTSIDE this hook (Quality's own cluster, not yet
//     extracted — a future QualityHost owns them). Every content-changing
//     edit calls `onQualityStale()` instead of re-evaluating quality itself,
//     so quality ownership can move without EditorHost ever holding a
//     quality/policy prop. This REPLACES the old `refreshQuality(materialId)`
//     helper (now `AppShellHost`'s zero-arg `onEditorQualityStale`).
//   - The material-load fan-out (5 parallel port calls in one Promise.all)
//     stays SHELL-OWNED (per the extraction plan's explicit recommendation —
//     less churn, keeps the single Promise.all). The shell calls this hook's
//     `resetForMaterialSwitch`/`applyLoadedMaterial`/`setMaterialError`/
//     `setProposals` to hand the load's results down, instead of writing
//     `elements`/`proposals`/etc. state that no longer lives in the shell.
//   - §4b (Chat → Editor): a landed proposal from a compose turn is applied
//     via `applyLandedProposal(materialId, landed)`, which returns whether
//     THIS hook instance owns that material right now (`materialId ===
//     activeMaterialId`) — false means the shell must hold the proposal in
//     its own "pending by material" read-model instead of losing it. Chat's
//     own extraction (a future ChatHost) doesn't exist yet; `submitText`
//     stays in AppShellHost and calls this instead of reaching into `loop`
//     directly.

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { useRef, useState } from "react";
import {
    type ComposeLoop,
    emptyLoop,
    loopAfterReview,
    loopAfterSubmit,
} from "../../app/composeLoop.js";
import type { ExternalError } from "../../app/externalError.js";
import type {
    LandedProposal,
    LoadedMaterial,
    MaterialProvenance,
} from "../../app/ports.js";
import {
    anchorForMove,
    applyAcceptedProposal,
    applyEdit,
    markChoiceCorrect,
    moveChoice,
    setChoiceFeedback,
    setElementText,
    unmarkChoiceCorrect,
} from "../Editor/edit.js";
import type { RuleEffect, SlashTriggerState } from "../Editor/inputRules.js";
import type {
    SlashMenuState,
    SlashTrigger,
} from "../Editor/MaterialDocument/MaterialDocument.js";
import type { InsertIntent } from "../Editor/Palette/Palette.js";
import type { LoadedProposal } from "../Editor/ProposalReview/ProposalReview.js";
import {
    matchSlash,
    resolveSlashItems,
    type SlashItem,
} from "../Editor/SlashMenu/slashItems.js";
import type {
    Choice,
    Element,
    ElementId,
    ElementKind,
    ReviewOutcome,
} from "../Editor/types.js";
import {
    ACCEPT_OUTCOMES,
    affectedCorrectness,
    editorShape,
    primaryTextOf,
    samePath,
} from "./edits.js";
import type { EditorHostApi, EditorHostProps, Snapshot } from "./types.js";

export type { EditorHostApi, EditorHostProps } from "./types.js";

export const useEditorHost = (props: EditorHostProps): EditorHostApi => {
    const {
        ports,
        activeMaterialId,
        dispatch,
        onQualityStale,
        showNotice,
        closeOverlay,
    } = props;
    const { t, i18n } = useLingui();

    const [elements, setElements] = useState<readonly Element[] | null>(null);
    // The optimistic-lock guard for manual edits (seeded on material load,
    // swapped for the server's returned hash after each successful edit) and the
    // id→path map a manual edit looks the target's path up in. Both reset on a
    // material switch; both are empty until the first load lands.
    const [contentHash, setContentHash] = useState("");
    const [pathById, setPathById] = useState<
        ReadonlyMap<ElementId, readonly string[]>
    >(new Map());
    const [materialError, setMaterialError] = useState<ExternalError | null>(
        null,
    );
    // The active material's origin ("Created from" + creation moment) —
    // loaded with the content, reset on every material switch.
    const [materialProvenance, setMaterialProvenance] =
        useState<MaterialProvenance | null>(null);
    const [loop, setLoop] = useState<ComposeLoop>(emptyLoop);
    // Real undo/redo: stacks of {elements, loop} snapshots. A content-changing
    // action pushes the pre-change snapshot onto `past` and clears `future`;
    // undo/redo move a snapshot between the two and restore it.
    const [history, setHistory] = useState<{
        readonly past: readonly Snapshot[];
        readonly future: readonly Snapshot[];
    }>({ past: [], future: [] });
    // The review queue: PENDING agent-edit proposals for the active material,
    // and the id of the one whose accept/reject is in flight (disables its
    // buttons). Refreshed on material switch and after each decision.
    const [proposals, setProposals] = useState<readonly LoadedProposal[]>([]);
    const [reviewPendingId, setReviewPendingId] = useState<string | null>(null);

    // Monotonic id source for locally-created elements (impure edge).
    const nextElementSeq = useRef(0);

    // Snapshot the current editor state before a content change (elements must be
    // loaded to change them). Clears the redo stack — a new edit forks history.
    const commit = (prevElements: readonly Element[], prevLoop: ComposeLoop) =>
        setHistory((h) => ({
            past: [...h.past, { elements: prevElements, loop: prevLoop }],
            future: [],
        }));

    const undo = () => {
        const prev = history.past.at(-1);
        if (prev === undefined || elements === null) return;
        setElements(prev.elements);
        setLoop(prev.loop);
        setHistory((h) => ({
            past: h.past.slice(0, -1),
            future: [...h.future, { elements, loop }],
        }));
    };
    const redo = () => {
        const next = history.future.at(-1);
        if (next === undefined || elements === null) return;
        setElements(next.elements);
        setLoop(next.loop);
        setHistory((h) => ({
            past: [...h.past, { elements, loop }],
            future: h.future.slice(0, -1),
        }));
    };

    // Roll a refused optimistic edit back: restore the pre-edit tree + guard
    // hash, drop the undo entry the edit pushed, and surface the refusal. Shared
    // by every optimistic-persist handler below (the single-command ones go
    // through `runStructuralEdit`; the multi-command ones call this directly).
    const rollback = (prevElements: readonly Element[], prevHash: string) => {
        setElements(prevElements);
        setContentHash(prevHash);
        setHistory((h) => ({ ...h, past: h.past.slice(0, -1) }));
        showNotice({ type: "edit-failed" });
    };

    // A fresh block element of the chosen kind, with sensible defaults. Ids are
    // minted here (the impure edge); edit.ts stays pure.
    const mkBlock = (kind: ElementKind, id: ElementId): Element => {
        switch (kind) {
            case "heading":
                return { type: "heading", id, level: 2, text: t`New heading` };
            case "prose":
                return { type: "prose", id, markdown: t`New paragraph.` };
            case "objective":
                return { type: "objective", id, text: t`New objective` };
            case "mcq":
                return {
                    type: "mcq",
                    id,
                    stem: t`New question`,
                    answerMode: "single",
                    choices: [
                        { id: `${id}-a`, text: t`First option`, correct: true },
                        {
                            id: `${id}-b`,
                            text: t`Second option`,
                            correct: false,
                        },
                    ],
                };
            default:
                return exhaustSwitchCase(kind);
        }
    };

    // The element whose primary EditableText should auto-enter edit mode +
    // take focus the moment it next mounts (Bug 1 fix, 2026-08-19): every
    // insert/retype dispatch below sets this to the new/retyped block's id
    // so the author keeps typing seamlessly instead of the caret dropping
    // to nowhere. Cleared once that EditableText reports the signal
    // consumed (its own one-shot contract — see types.ts).
    const [autoEditElementId, setAutoEditElementId] =
        useState<ElementId | null>(null);
    const onAutoEditConsumed = () => setAutoEditElementId(null);

    // Insert from the palette: media is the deferred wiring slice (a notice); a
    // block is minted, appended OPTIMISTICALLY to the local AST, then PERSISTED
    // through the same `insert_child_element` primitive the agent uses. On
    // success the block's domain path is threaded into `pathById` (so it becomes
    // immediately text-editable) and the new content hash swapped in; on failure
    // the optimistic insert is rolled back and the refusal surfaced. Without this
    // persistence the insert vanished on reload (the P23 E1 break).
    const onInsert = (intent: InsertIntent) => {
        if (elements === null) {
            closeOverlay();
            return;
        }
        if (intent.type === "media") {
            showNotice({ type: "media" });
            closeOverlay();
            return;
        }
        const id: ElementId = `new-${nextElementSeq.current}`;
        nextElementSeq.current += 1;
        const block = mkBlock(intent.kind, id);
        const prevElements = elements;
        const prevHash = contentHash;
        commit(elements, loop);
        setElements(
            applyEdit(elements)({
                type: "insert-element",
                element: block,
                index: elements.length,
            }),
        );
        setAutoEditElementId(id);
        closeOverlay();
        // No active material to persist against (unreachable from the editor
        // palette, but keeps the port call total) — the local insert stands.
        if (activeMaterialId === null) return;
        const run = async () => {
            const r = await ports.insertBlock(
                activeMaterialId,
                block,
                prevHash,
            );
            if (Result.isErr(r)) return rollback(prevElements, prevHash);
            setContentHash(r.value.contentHash);
            setPathById((prev) => new Map([...prev, [id, r.value.path]]));
        };
        void run();
    };

    // A live markdown input-rule (`# `/`## `/`### `) fired while typing a
    // prose block. `deferred` names a rule that matched but has no Element
    // kind to land in yet (bullet/numbered lists — inputRules.ts's stated
    // MVP shortcut) — surfaced as the same "can't do that yet" notice the
    // palette's media row uses, never silently dropped. A `retype` effect
    // applies OPTIMISTICALLY (the pure `retype-element` fold — SAME id, only
    // `type`/`text`/`attrs` change) then PERSISTS through the domain's
    // `retype_element` primitive; on failure it rolls back exactly like
    // `onEditText`. `id` is the block the rule fired in (MaterialDocument's
    // per-element wrapper); `effect.command.id` names the same block — using
    // the explicit param keeps this in step with the other by-id handlers.
    const onInputRule = (id: ElementId, effect: RuleEffect) => {
        if (effect.type === "deferred") {
            showNotice({ type: "edit-failed" });
            return;
        }
        if (activeMaterialId === null || elements === null) return;
        const path = pathById.get(id);
        if (path === undefined) return;
        const to = effect.command.to;
        // The retyped block mounts a BRAND-NEW EditableText (a different
        // renderer takes over the same id) — keep the author typing into it.
        setAutoEditElementId(id);
        runStructuralEdit(
            applyEdit(elements)({ type: "retype-element", id, to }),
            (materialId, hash) =>
                ports.retypeElement(materialId, path, to, hash),
        );
    };

    // The `/` slash-trigger's live state (MaterialDocument.SlashTrigger):
    // tracks AT MOST one prose block — the one currently being typed into —
    // plus the query typed after the leading `/` (drives the popup's live
    // filter) and the keyboard cursor into the filtered list. `null` ⇒ no
    // block is tracked (every prose block's EditableText reads "closed").
    const [slashTrigger, setSlashTrigger] = useState<
        (SlashTrigger & { readonly query: string }) | null
    >(null);
    const [slashActiveIndex, setSlashActiveIndex] = useState(0);

    // Report a keystroke's slash-trigger transition UP from the tracked
    // block's EditableText. "closed" clears the tracked element entirely;
    // "open"/"dismissed" replace it (a prose block can only be typed into
    // one at a time, so there is never more than one tracked block).
    const onSlashTrigger = (
        id: ElementId,
        state: SlashTriggerState,
        query: string,
    ) => {
        setSlashTrigger(
            state === "closed" ? null : { forElementId: id, state, query },
        );
        if (state === "open") setSlashActiveIndex(0);
    };

    // A slash-menu row was picked: mint a fresh block of the chosen kind and
    // insert it AFTER the tracked block — OPTIMISTICALLY (the pure
    // `insert-element` fold), then PERSISTED through the domain's sibling-
    // relative `insert_element` primitive (`insertBlockAfter`, the twin of
    // `onInsert`'s `insertBlock` which always appends at the document's
    // end). Mirrors `onInsert`'s rollback + `pathById` threading exactly, so
    // the fresh block becomes immediately text-editable. Media rows are the
    // same deferred slice `onInsert` already surfaces.
    const onSlashCommit = (item: SlashItem) => {
        const anchor = slashTrigger;
        setSlashTrigger(null);
        if (anchor === null || elements === null) return;
        if (item.intent.type === "media") {
            showNotice({ type: "media" });
            return;
        }
        const anchorPath = pathById.get(anchor.forElementId);
        if (anchorPath === undefined) return; // not path-editable ⇒ no-op
        const id: ElementId = `new-${nextElementSeq.current}`;
        nextElementSeq.current += 1;
        const block = mkBlock(item.intent.kind, id);
        const anchorIndex = elements.findIndex(
            (e) => e.id === anchor.forElementId,
        );
        const at = anchorIndex === -1 ? elements.length : anchorIndex + 1;
        const prevElements = elements;
        const prevHash = contentHash;
        commit(elements, loop);
        setElements(
            applyEdit(elements)({
                type: "insert-element",
                element: block,
                index: at,
            }),
        );
        setAutoEditElementId(id);
        if (activeMaterialId === null) return;
        const run = async () => {
            const r = await ports.insertBlockAfter(
                activeMaterialId,
                anchorPath,
                block,
                prevHash,
            );
            if (Result.isErr(r)) return rollback(prevElements, prevHash);
            setContentHash(r.value.contentHash);
            setPathById((prev) => new Map([...prev, [id, r.value.path]]));
        };
        void run();
    };

    // Enter at the END of a top-level text block ⇒ a new prose block below (the
    // document-first "new line is a new block" gesture; a true mid-text split is
    // deferred, document-first-editor.md §4). The rented caret hands up the text
    // it just committed, so this persists TWO edits as ONE sequenced chain — the
    // anchor block's text update (only when it actually changed) THEN the sibling
    // insert — threading each returned hash into the next so neither races the
    // optimistic lock. Optimistic-apply both to the local tree, roll BOTH back on
    // any refusal, and thread the new block's domain path into `pathById` so it is
    // immediately text-editable. An anchor with no path (pqbl course-object
    // content) is not path-editable ⇒ no-op.
    const onEnterBlockBelow = (id: ElementId, committedText: string) => {
        if (activeMaterialId === null || elements === null) return;
        const anchorPath = pathById.get(id);
        if (anchorPath === undefined) return; // not path-editable ⇒ no-op
        const anchor = elements.find((e) => e.id === id);
        if (anchor === undefined) return;
        const textChanged = primaryTextOf(anchor) !== committedText;
        const materialId = activeMaterialId;
        const newId: ElementId = `new-${nextElementSeq.current}`;
        nextElementSeq.current += 1;
        // A fresh EMPTY paragraph — the author keeps typing into it (autoEdit),
        // exactly the Notion "Enter drops you into a new block" feel.
        const block: Element = { type: "prose", id: newId, markdown: "" };
        const anchorIndex = elements.findIndex((e) => e.id === id);
        const at = anchorIndex === -1 ? elements.length : anchorIndex + 1;
        dispatch({ type: "resource-edited", id: materialId });
        const prevElements = elements;
        const prevHash = contentHash;
        commit(elements, loop);
        // Optimistic: fold the (possible) text edit, then the sibling insert.
        const afterText = textChanged
            ? setElementText(elements, id, committedText)
            : elements;
        setElements(
            applyEdit(afterText)({
                type: "insert-element",
                element: block,
                index: at,
            }),
        );
        setAutoEditElementId(newId);
        const run = async () => {
            let hash = prevHash;
            if (textChanged) {
                const rText = await ports.updateElementText(
                    materialId,
                    anchorPath,
                    committedText,
                    hash,
                );
                if (Result.isErr(rText))
                    return rollback(prevElements, prevHash);
                hash = rText.value.contentHash;
            }
            const rIns = await ports.insertBlockAfter(
                materialId,
                anchorPath,
                block,
                hash,
            );
            if (Result.isErr(rIns)) return rollback(prevElements, prevHash);
            setContentHash(rIns.value.contentHash);
            setPathById((prev) => new Map([...prev, [newId, rIns.value.path]]));
            onQualityStale();
        };
        void run();
    };

    // Backspace on an EMPTY top-level block ⇒ remove it and focus the previous
    // (the document-first "delete an empty line" gesture; a text-MERGE into the
    // previous block is deferred, §4). Only when there IS a previous block —
    // Backspace in the first/only block is a no-op, so the document can never be
    // emptied by this gesture. Reuses the `remove_element` primitive + the shared
    // rollback runner; the previous block gets the autoEdit focus signal.
    const onDeleteEmptyBlock = (id: ElementId) => {
        if (elements === null) return;
        const index = elements.findIndex((e) => e.id === id);
        if (index <= 0) return; // first/only block ⇒ nothing to merge into
        const path = pathById.get(id);
        if (path === undefined) return; // not path-editable ⇒ no-op
        const prev = elements[index - 1];
        runStructuralEdit(
            applyEdit(elements)({ type: "remove-element", id }),
            (materialId, hash) => ports.removeElement(materialId, path, hash),
        );
        if (prev !== undefined) setAutoEditElementId(prev.id);
    };

    // Resolve an in-editor review at an element. If a REAL pending proposal
    // backs this element (matched by domain path against the loaded proposal
    // queue), the decision goes through the SAME persisting commands the review
    // pane uses — accept applies + records the acceptance, reject discards — so
    // the resolution survives a reload (previously this path called a port that
    // reported success while persisting NOTHING). Only the mock's landed-turn
    // wear (loop.annotations; the real turn lands none) has no backing proposal:
    // there we apply the optimistic AST transform for the design preview and
    // clear the wear, claiming no persistence.
    const onReview = (elementId: ElementId, outcome: ReviewOutcome) => {
        if (activeMaterialId === null || elements === null) return;
        const path = pathById.get(elementId);
        const backing =
            path !== undefined
                ? proposals.find((p) => samePath(p.path, path))
                : undefined;
        if (backing !== undefined) {
            if (ACCEPT_OUTCOMES.has(outcome.type))
                onAcceptProposal(backing.proposalId);
            else onRejectProposal(backing.proposalId);
            return;
        }
        const proposal = loop.annotations.get(elementId)?.proposal;
        commit(elements, loop);
        if (proposal !== undefined)
            setElements(
                applyAcceptedProposal(elements, elementId, proposal, outcome),
            );
        setLoop(loopAfterReview(loop, elementId));
    };

    // Approve a pending agent proposal: the server applies its patch to content
    // (the SAME edit primitive a manual edit uses) and records acceptance. On
    // success the material has CHANGED, so reload its content + the queue, and
    // signal quality is stale (Quality's own cluster re-evaluates — see the
    // "CROSS-CLUSTER SEAMS" note; the original combined content+quality+policy
    // Promise.all is split here so EditorHost never holds a quality/policy
    // setter). Distinct from reject — its own command
    // (`/commands/acceptProposal`).
    const onAcceptProposal = (proposalId: string) => {
        if (activeMaterialId === null) return;
        setReviewPendingId(proposalId);
        const run = async () => {
            const r = await ports.acceptProposal(proposalId);
            if (Result.isErr(r)) {
                showNotice({ type: "edit-failed" });
                setReviewPendingId(null);
                return;
            }
            const [contentR, proposalsR] = await Promise.all([
                ports.loadMaterial(activeMaterialId),
                ports.loadProposals(activeMaterialId),
            ]);
            if (Result.isOk(contentR)) {
                setElements(contentR.value.elements);
                setContentHash(contentR.value.contentHash);
                setPathById(contentR.value.pathById);
                setHistory({ past: [], future: [] });
            }
            setProposals(
                Result.isOk(proposalsR)
                    ? proposalsR.value
                    : proposals.filter((p) => p.proposalId !== proposalId),
            );
            setReviewPendingId(null);
            onQualityStale();
        };
        void run();
    };

    // Reject a pending agent proposal: discard it, no content change
    // (`/commands/rejectProposal`). Drop it from the queue optimistically.
    const onRejectProposal = (proposalId: string) => {
        if (activeMaterialId === null) return;
        setReviewPendingId(proposalId);
        const run = async () => {
            const r = await ports.rejectProposal(proposalId);
            if (Result.isErr(r)) {
                showNotice({ type: "edit-failed" });
                setReviewPendingId(null);
                return;
            }
            setProposals((ps) => ps.filter((p) => p.proposalId !== proposalId));
            setReviewPendingId(null);
        };
        void run();
    };

    // Manual inline text edit at an element (or a choice): the single-command
    // optimistic-persist dance (see `runStructuralEdit`) around the pure
    // `setElementText` fold, persisted through the SAME `update_element_text`
    // primitive the agent uses. An element with no path (a pqbl/prose material's
    // synthesised id) is not path-editable: no-op.
    const onEditText = (elementId: ElementId, newText: string) => {
        if (elements === null) return;
        const path = pathById.get(elementId);
        if (path === undefined) return;
        runStructuralEdit(
            setElementText(elements, elementId, newText),
            (materialId, hash) =>
                ports.updateElementText(materialId, path, newText, hash),
        );
    };

    // Manual per-choice feedback edit: the twin of `onEditText`, routed through
    // `update_element_attrs` (`{ feedback }`).
    const onEditFeedback = (choiceId: ElementId, feedback: string) => {
        if (elements === null) return;
        const path = pathById.get(choiceId);
        if (path === undefined) return;
        runStructuralEdit(
            setChoiceFeedback(elements, choiceId, feedback),
            (materialId, hash) =>
                ports.updateElementAttrs(materialId, path, { feedback }, hash),
        );
    };

    // Persist a correctness change: diff the pre/post trees for every choice
    // whose `correct` flag moved, then PATCH each through the keyed-merge
    // primitive (`patch_element_attrs`, `{ correct }`) so the write never
    // clobbers the choice's `feedback` — the whole point of the merge verb. On a
    // single-answer MCQ the fold cleared the prior key, so the diff yields two
    // affected choices (new key true + old key false); each gets its own patch,
    // SEQUENCED so the second guards on the first's fresh hash. Optimistic-apply
    // the whole change, roll the tree + hash back on any refusal. Every affected
    // choice must be path-editable; if any lacks a path the change is a no-op
    // (mirrors onEditText / onEditFeedback — content that is not path-editable).
    const persistChoiceCorrectness = (next: readonly Element[]) => {
        if (activeMaterialId === null || elements === null) return;
        const affected = affectedCorrectness(elements, next);
        if (affected.length === 0) return; // no-op fold (unknown id / no change)
        // Edit-must-keep: promotes the glance if this material is it (no-op else).
        dispatch({ type: "resource-edited", id: activeMaterialId });
        const targets = affected.map((a) => ({
            path: pathById.get(a.choiceId),
            correct: a.correct,
        }));
        if (targets.some((t) => t.path === undefined)) return; // not path-editable
        const prevElements = elements;
        const prevHash = contentHash;
        commit(elements, loop);
        setElements(next);
        const run = async () => {
            let hash = contentHash;
            for (const target of targets) {
                if (target.path === undefined)
                    return rollback(prevElements, prevHash); // guarded above
                const r = await ports.patchElementAttrs(
                    activeMaterialId,
                    target.path,
                    { correct: target.correct },
                    hash,
                );
                if (Result.isErr(r)) return rollback(prevElements, prevHash);
                hash = r.value.contentHash;
            }
            setContentHash(hash);
            onQualityStale();
        };
        void run();
    };

    // Mark / unmark a choice as the correct answer — TWO intents, two commands
    // (never a setCorrect(boolean) flag). Each resolves the owning MCQ by choice
    // id through the pure fold (which enforces "exactly one key" on single-answer
    // items), then persists the diff. A single-answer radio only ever reaches
    // the mark path; unmark is wired for the deferred multiple-answer control.
    const onMarkChoiceCorrect = (choiceId: ElementId) => {
        if (elements === null) return;
        persistChoiceCorrectness(markChoiceCorrect(elements, choiceId));
    };
    const onUnmarkChoiceCorrect = (choiceId: ElementId) => {
        if (elements === null) return;
        persistChoiceCorrectness(unmarkChoiceCorrect(elements, choiceId));
    };

    // The shared optimistic-persist runner behind every SINGLE-command structural
    // edit (move / remove a block, add / remove / reorder a choice, set a heading
    // level or answer mode). Snapshot for undo, swap `next` in optimistically,
    // run `persist` (the port call, guarded by the current hash), swap the new
    // hash in on success or roll the whole thing back on refusal — the same dance
    // onEditText / onEditFeedback do, factored so each new handler is one line.
    // `persist` receives the (non-null) material id + the guard hash so it never
    // reaches for a possibly-null closure. Multi-command edits (answer-mode single,
    // correctness) keep their own sequenced runners.
    const runStructuralEdit = (
        next: readonly Element[],
        persist: (
            materialId: string,
            expectedContentHash: string,
        ) => Promise<Result.Result<{ readonly contentHash: string }, unknown>>,
    ) => {
        if (activeMaterialId === null || elements === null) return;
        const materialId = activeMaterialId;
        dispatch({ type: "resource-edited", id: materialId });
        const prevElements = elements;
        const prevHash = contentHash;
        commit(elements, loop);
        setElements(next);
        const run = async () => {
            const r = await persist(materialId, prevHash);
            if (Result.isErr(r)) return rollback(prevElements, prevHash);
            setContentHash(r.value.contentHash);
            onQualityStale();
        };
        void run();
    };

    // Move a block to `toIndex` (index in the list with the block removed). The
    // domain move primitive is sibling-relative, so translate the index into an
    // anchor + before/after with `anchorForMove`, then persist through the same
    // `move_element` the agent uses. A one-block list (no anchor) or an
    // unresolvable path (pqbl course-object content) is a no-op.
    const onMoveElement = (id: ElementId, toIndex: number) => {
        if (elements === null) return;
        const anchor = anchorForMove(elements, id, toIndex);
        if (anchor === undefined) return;
        const path = pathById.get(id);
        const targetAnchorPath = pathById.get(anchor.anchorId);
        if (path === undefined || targetAnchorPath === undefined) return;
        runStructuralEdit(
            applyEdit(elements)({ type: "move-element", id, toIndex }),
            (materialId, hash) =>
                ports.moveElement(
                    materialId,
                    path,
                    targetAnchorPath,
                    anchor.position,
                    hash,
                ),
        );
    };

    // Remove a block through the `remove_element` primitive. Unresolvable path ⇒
    // no-op (mirrors the text handlers).
    const onRemoveElement = (id: ElementId) => {
        if (elements === null) return;
        const path = pathById.get(id);
        if (path === undefined) return;
        runStructuralEdit(
            applyEdit(elements)({ type: "remove-element", id }),
            (materialId, hash) => ports.removeElement(materialId, path, hash),
        );
    };

    // Append a fresh empty choice to an MCQ: mint its id (same seq as block
    // insert), apply `add-choice` optimistically, persist through
    // `insert_child_element`, then thread the returned path into `pathById` so the
    // new choice is immediately editable. The MCQ's own path must resolve (its
    // container entry in pathById); pqbl course-object content has none ⇒ no-op.
    const onAddChoice = (mcqId: ElementId) => {
        if (elements === null) return;
        const mcqPath = pathById.get(mcqId);
        if (mcqPath === undefined) return;
        const choiceId: ElementId = `new-${nextElementSeq.current}`;
        nextElementSeq.current += 1;
        const choice: Choice = { id: choiceId, text: "", correct: false };
        runStructuralEdit(
            applyEdit(elements)({ type: "add-choice", mcqId, choice }),
            async (materialId, hash) => {
                const r = await ports.addChoice(
                    materialId,
                    mcqPath,
                    choice,
                    hash,
                );
                if (Result.isOk(r)) {
                    setPathById(
                        (prev) => new Map([...prev, [choiceId, r.value.path]]),
                    );
                }
                return r;
            },
        );
    };

    // Remove one choice from an MCQ through `remove_element` (a choice IS an
    // element at `[...mcqPath, choiceId]`). Unresolvable path ⇒ no-op.
    const onRemoveChoice = (mcqId: ElementId, choiceId: ElementId) => {
        if (elements === null) return;
        const path = pathById.get(choiceId);
        if (path === undefined) return;
        runStructuralEdit(
            applyEdit(elements)({
                type: "remove-choice",
                mcqId,
                choiceId,
            }),
            (materialId, hash) => ports.removeElement(materialId, path, hash),
        );
    };

    // Reorder one choice within its MCQ to `toIndex` — the choice-list twin of
    // onMoveElement: translate the index to a sibling anchor among the MCQ's
    // choices, then persist through `move_element`.
    const onMoveChoice = (
        mcqId: ElementId,
        choiceId: ElementId,
        toIndex: number,
    ) => {
        if (elements === null) return;
        const mcq = elements.find((e) => e.id === mcqId);
        if (mcq === undefined || mcq.type !== "mcq") return;
        const anchor = anchorForMove(mcq.choices, choiceId, toIndex);
        if (anchor === undefined) return;
        const path = pathById.get(choiceId);
        const targetAnchorPath = pathById.get(anchor.anchorId);
        if (path === undefined || targetAnchorPath === undefined) return;
        runStructuralEdit(
            moveChoice(elements, choiceId, toIndex),
            (materialId, hash) =>
                ports.moveElement(
                    materialId,
                    path,
                    targetAnchorPath,
                    anchor.position,
                    hash,
                ),
        );
    };

    // Set a heading's level through the merge-safe `patch_element_attrs`
    // (`{ level }`) so it never clobbers other heading attrs. Unresolvable path ⇒
    // no-op.
    const onSetHeadingLevel = (id: ElementId, level: 1 | 2 | 3) => {
        if (elements === null) return;
        const path = pathById.get(id);
        if (path === undefined) return;
        runStructuralEdit(
            applyEdit(elements)({ type: "set-heading-level", id, level }),
            (materialId, hash) =>
                ports.patchElementAttrs(materialId, path, { level }, hash),
        );
    };

    // Widen an MCQ to multiple-answer: one merge-safe answerMode patch, no
    // correctness change. Unresolvable path ⇒ no-op.
    const onAllowMultipleAnswers = (mcqId: ElementId) => {
        if (elements === null) return;
        const path = pathById.get(mcqId);
        if (path === undefined) return;
        runStructuralEdit(
            applyEdit(elements)({ type: "allow-multiple-answers", mcqId }),
            (materialId, hash) =>
                ports.patchElementAttrs(
                    materialId,
                    path,
                    { answerMode: "multiple" },
                    hash,
                ),
        );
    };

    // Narrow an MCQ to single-answer: the fold sets the mode AND clears any extra
    // key (keeping the first). Persist BOTH — the answerMode attr on the MCQ, then
    // each affected choice's cleared `correct` — sequenced so every write guards on
    // the prior fresh hash, mirroring persistChoiceCorrectness. Optimistic-apply
    // the whole change; roll the tree + hash back on any refusal. The MCQ path and
    // every affected choice path must resolve, else no-op.
    const onAllowSingleAnswer = (mcqId: ElementId) => {
        if (activeMaterialId === null || elements === null) return;
        const mcqPath = pathById.get(mcqId);
        if (mcqPath === undefined) return;
        const next = applyEdit(elements)({
            type: "allow-single-answer",
            mcqId,
        });
        const cleared = affectedCorrectness(elements, next);
        const clearedTargets = cleared.map((a) => ({
            path: pathById.get(a.choiceId),
            correct: a.correct,
        }));
        if (clearedTargets.some((t) => t.path === undefined)) return;
        const materialId = activeMaterialId;
        dispatch({ type: "resource-edited", id: materialId });
        const prevElements = elements;
        const prevHash = contentHash;
        commit(elements, loop);
        setElements(next);
        const run = async () => {
            let hash = prevHash;
            const rMode = await ports.patchElementAttrs(
                materialId,
                mcqPath,
                { answerMode: "single" },
                hash,
            );
            if (Result.isErr(rMode)) return rollback(prevElements, prevHash);
            hash = rMode.value.contentHash;
            for (const target of clearedTargets) {
                if (target.path === undefined)
                    return rollback(prevElements, prevHash);
                const r = await ports.patchElementAttrs(
                    materialId,
                    target.path,
                    { correct: target.correct },
                    hash,
                );
                if (Result.isErr(r)) return rollback(prevElements, prevHash);
                hash = r.value.contentHash;
            }
            setContentHash(hash);
            onQualityStale();
        };
        void run();
    };

    // The resolved slash-menu POPUP for the tracked block, once its trigger
    // is "open" — items are the template's insert allow-list (the SAME
    // `editorShape` the toolbar's Palette reads), live-filtered by the typed
    // query. `undefined` while closed/dismissed ⇒ MaterialDocument renders no
    // popup (its own progressive-disclosure default).
    const slashMenuState: SlashMenuState | undefined =
        slashTrigger !== null && slashTrigger.state === "open"
            ? {
                  forElementId: slashTrigger.forElementId,
                  items: matchSlash(
                      resolveSlashItems(editorShape, i18n),
                      slashTrigger.query,
                  ),
                  activeIndex: slashActiveIndex,
                  variant: "labelled",
                  onCommit: onSlashCommit,
                  onHoverIndex: setSlashActiveIndex,
              }
            : undefined;

    // Keyboard nav of the open slash menu — the ↑/↓/Enter arm of the "keyboard
    // is a joy" contract (the mouse arm is SlashMenu's own onMouseDown). Move
    // wraps around the filtered list; commit picks the active row and routes to
    // the same `onSlashCommit` a click uses. Both no-op when the menu is closed
    // (no `slashMenuState`) or empty.
    const onSlashNavigate = (direction: "up" | "down") => {
        const menu = slashMenuState;
        if (menu === undefined) return;
        const count = menu.items.length;
        if (count === 0) return;
        setSlashActiveIndex((i) => {
            const next = direction === "down" ? i + 1 : i - 1;
            return ((next % count) + count) % count; // wrap both ends
        });
    };
    const onSlashCommitActive = () => {
        const menu = slashMenuState;
        if (menu === undefined) return;
        const item = menu.items[slashActiveIndex];
        if (item === undefined) return;
        onSlashCommit(item);
    };

    // === Material-load wiring (called by the shell's fan-out effect) =========
    // The fan-out itself (loadMaterial/loadConversation/evaluateMaterial/
    // evaluateMaterialPolicy/loadProposals in one Promise.all) stays
    // shell-owned (.claude/analysis/ui-ddd-decomposition.md §6 Phase 3) — these
    // just receive its results for the pieces EditorHost owns.

    // On a genuine material switch, clear every editor-owned piece (quality/
    // policy/conversation are the shell's own resets, called alongside this).
    const resetForMaterialSwitch = () => {
        setElements(null);
        setContentHash("");
        setPathById(new Map());
        setMaterialProvenance(null);
        setLoop(emptyLoop);
        setHistory({ past: [], future: [] });
        setProposals([]);
    };
    const applyLoadedMaterial = (loaded: LoadedMaterial) => {
        setElements(loaded.elements);
        setContentHash(loaded.contentHash);
        setPathById(loaded.pathById);
        setMaterialProvenance(loaded.provenance);
    };

    // §4b: fold a compose turn's landed proposal into `loop`, snapshotting for
    // undo — but only when THIS instance owns the target material right now
    // (it always does today, since `submitText`'s target falls back to
    // `activeMaterialId` first; the explicit check is what keeps this correct
    // once a real ChatHost can target an material whose editor isn't mounted).
    const applyLandedProposal = (
        materialId: string,
        landed: LandedProposal,
    ): boolean => {
        if (materialId !== activeMaterialId) return false;
        setLoop((prev) =>
            loopAfterSubmit(prev, landed.targetId, landed.proposal),
        );
        if (elements !== null) commit(elements, loop);
        return true;
    };

    return {
        elements,
        contentHash,
        pathById,
        materialError,
        materialProvenance,
        loop,
        history,
        proposals,
        reviewPendingId,
        resetForMaterialSwitch,
        applyLoadedMaterial,
        setMaterialError,
        setProposals,
        undo,
        redo,
        onEditText,
        onEditFeedback,
        onMarkChoiceCorrect,
        onUnmarkChoiceCorrect,
        onAddChoice,
        onRemoveChoice,
        onMoveChoice,
        onSetHeadingLevel,
        onMoveElement,
        onRemoveElement,
        onAllowMultipleAnswers,
        onAllowSingleAnswer,
        onInputRule,
        onInsert,
        onReview,
        onEnterBlockBelow,
        onDeleteEmptyBlock,
        onAcceptProposal,
        onRejectProposal,
        slashTrigger,
        onSlashTrigger,
        onSlashCommit,
        onSlashNavigate,
        onSlashCommitActive,
        slashMenuState,
        autoEditElementId,
        onAutoEditConsumed,
        editorShape,
        applyLandedProposal,
    };
};
